import { Component } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Dictionary } from '@ngrx/entity';
import { TranslateService } from '@ngx-translate/core';
import {
  LOCAL_STORAGE_KEYS,
  PanAppState,
  PlatformService,
} from '@panamax/app-state';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { FEATURES } from '@shared/constants/splitio-features';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { Customer } from '@usf/customer-types';
import { InvoiceFilterIds } from '@usf/ngrx-payment';
import { PaymentTransactionsViewModel, Status } from '@usf/payment-types';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PATHS } from '../shared/constants/paths';
import {
  INITIAL_TOAST_ERROR_MESSAGE,
  MAX_INVOICE_SELECTION,
  OPEN_AUTOPAY_MODAL,
} from './constants/constants';
import { PaymentTile } from './models/payment-tile';
import {
  RecentInvoicesCreditsText,
  TransactionRowText,
} from './models/payment-transaction';
import { PaymentsPageViewModel } from './models/payments-page-view.model';
import { PaymentsBasePage } from './payments-shared/payments-base/payments-base.page';
import { CancelPendingPaymentsService } from './services/cancel-pending-payments/cancel-pending-payments.service';
import { InvoiceIsrService } from './services/invoice-isr.service';
import { PaymentsService } from './services/payments.service';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.page.html',
  styleUrls: ['./payments.page.scss'],
})
export class PaymentsPage extends PaymentsBasePage {
  backgroundImageStyle = "url('/assets/images/payments-hero.jpg')";
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.8) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0.5) 40%, rgba(47,47,47,0.3) 53%, rgba(47,47,47,0) 77%, rgba(47,47,47,0) 89%, rgba(47,47,47,0) 100%)';
  paymentsPageVM$: Observable<PaymentsPageViewModel>;
  invoicesTile: PaymentTile;
  invoicesAndStatementsTile: PaymentTile;
  paymentActivityTile: PaymentTile;
  paymentPreferencesTile: PaymentTile;
  makePaymentTile: PaymentTile;
  comingSoonTile: PaymentTile;
  comingSoonPaymentTile: PaymentTile;
  recentInvoicesAndCreditsText: RecentInvoicesCreditsText;
  invoices: PaymentTransactionsViewModel[];
  credits: PaymentTransactionsViewModel[];
  headerCustomer: Customer;
  featureFlagPayments$: Subscription;
  paymentsPageVMSub$: Subscription;
  transactionRowText: TransactionRowText;
  maxTransactionsSelectedMessage: string;
  showMakePayments = false;
  isPaymentActivityEnabled = false;
  isPaymentPreferencesEnabled = false;
  isARStatementsEnabled = false;
  isARStatementsNewBadgeEnabled = false;
  checkFlagByDivisionSub$: Subscription;
  showOrderNumber: boolean;

  constructor(
    public router: Router,
    readonly route: ActivatedRoute,
    readonly paymentsService: PaymentsService,
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    private translateService: TranslateService,
    private invoiceIsrService: InvoiceIsrService,
    private loadingSpinnerService: LoadingSpinnerService,
    private cancelPaymentsService: CancelPendingPaymentsService,
  ) {
    super(router, route, paymentsService);
  }

  ionViewWillEnter(): void {
    super.ionViewWillEnter();

    this.paymentsService.setInitialInvoicesSortAndFilter();
    this.paymentsService.getEligibleADCustomers();

    this.setPaymentTileText();

    this.paymentsPageVM$ = this.paymentsService.getPaymentsVM$();

    this.paymentsPageVMSub$ = this.paymentsPageVM$
      .pipe(filter(vm => vm.customers?.length > 0 && !!vm.headerCustomer))
      .subscribe(vm => {
        this.paymentsService.showPaymentsOutageMessageIfNeeded(
          vm?.paymentHasOutage,
          this.translateService.instant('i18n.payments.outageMessage'),
        );

        // Add log for LogRocket
        console.log('paymentsPageVM', vm.canPayInvoice, vm);

        this.panAppState.setPageTitle(
          vm?.canPayInvoice
            ? this.translateService.instant(PageTitle.invoicesAndPayments)
            : this.translateService.instant(PageTitle.paymentsNoFlag),
        );
        if (!this.headerCustomer) {
          if (localStorage.getItem(LOCAL_STORAGE_KEYS.paymentCustomer)) {
            const localPaymentCustomer: number[] = JSON.parse(
              localStorage.getItem(LOCAL_STORAGE_KEYS.paymentCustomer),
            );
            const customers = vm?.customers
              .filter(customer =>
                localPaymentCustomer?.includes(customer.customerNumber),
              )
              .map(customer => customer?.customerNumber);
            if (customers?.length) {
              this.paymentsService.switchPaymentCustomer(customers);
            }
          } else {
            const customers = vm?.selectedCustomer?.map(
              customer => customer?.customerNumber,
            );
            this.paymentsService.getAccountSummaryInvoicesAndCredits(customers);
          }
        } else if (
          this.headerCustomer?.customerNumber !==
            vm.headerCustomer?.customerNumber &&
          !!vm.headerCustomer?.customerNumber
        ) {
          this.paymentsService.switchPaymentCustomer([
            vm.headerCustomer?.customerNumber,
          ]);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.paymentCustomer);
        }
        this.headerCustomer = vm?.headerCustomer;
        this.makePaymentTile.badgeText = vm?.countOfOpenInvoices
          ? this.translateService.instant(
              'i18n.payments.invoicesAndCredits.badgeText',
              {
                unpaidInvoices: vm?.countOfOpenInvoices,
              },
            )
          : undefined;
      });

    this.checkPaymentsFeatureFlags();
  }

  ionViewDidEnter() {
    this.paymentsService.trackPageLoad();
  }

  ionViewWillLeave() {
    this.headerCustomer = null;
    this.paymentsPageVMSub$?.unsubscribe();
    this.checkFlagByDivisionSub$?.unsubscribe();
    this.featureFlagPayments$?.unsubscribe();
  }

  checkPaymentsFeatureFlags() {
    this.featureFlagPayments$ = combineLatest([
      this.panAppState.feature$([
        FEATURES.split_user_payments_make_a_payment.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_payment_activity.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_payment_preferences.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_ar_statements.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_user_payments_ar_statements_new_badge.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_payments_order_number.name,
      ]),
      this.panAppState.user$,
    ]).subscribe(
      ([
        isMakePaymentEnabled,
        isPaymentActivityEnabled,
        isPaymentPreferencesEnabled,
        isARStatementsEnabled,
        isARStatementsNewBadgeEnabled,
        orderNumberFlag,
        userState,
      ]) => {
        if (userState?.userId) {
          if (isMakePaymentEnabled) {
            this.showMakePayments = true;
          }
          if (isPaymentActivityEnabled) {
            this.isPaymentActivityEnabled = true;
          }

          if (isPaymentPreferencesEnabled) {
            this.isPaymentPreferencesEnabled = true;
          }

          if (isARStatementsEnabled) {
            this.isARStatementsEnabled = true;
          }

          if (isARStatementsNewBadgeEnabled) {
            this.isARStatementsNewBadgeEnabled = true;
          }

          if (orderNumberFlag) {
            this.showOrderNumber = true;
          }

          if (!isMakePaymentEnabled && userState.assignedCustomers) {
            const uniqueDivisions = userState.assignedCustomers
              .map(customer => customer.divisionNumber)
              .filter(
                (divisionNumber, index, divisions) =>
                  divisions.indexOf(divisionNumber) === index,
              );
            this.checkFlagByDivisionSub$ = this.paymentsService
              .checkFlagByDivisions(
                FEATURES.split_division_payments_make_a_payment.name,
                uniqueDivisions,
              )
              .subscribe(isOn => {
                if (isOn) {
                  this.showMakePayments = true;
                }
              });
          }
        }
      },
    );
  }

  private setPaymentTileText() {
    this.translateService
      .get('i18n.payments.invoicesAndCredits.title')
      .pipe(take(1))
      ?.subscribe(_ => {
        this.invoicesTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.invoicesAndCredits.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.invoicesAndCredits.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.invoicesAndCredits.textButton',
          ),
          badgeText: this.translateService.instant(
            'i18n.payments.invoicesAndCredits.badgeText',
            {
              unpaidInvoices: 2,
            },
          ),
        };

        this.invoicesAndStatementsTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.invoicesCreditsAndStatements.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.invoicesCreditsAndStatements.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.invoicesCreditsAndStatements.textButton',
          ),
          badgeText: this.translateService.instant(
            'i18n.payments.invoicesCreditsAndStatements.badgeText',
          ),
        };

        this.comingSoonTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.comingSoon.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.comingSoon.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.comingSoon.textButton',
          ),
          iconButton: this.translateService.instant(
            'i18n.payments.comingSoon.iconButton',
          ),
        };

        this.comingSoonPaymentTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.comingSoonPayment.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.comingSoonPayment.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.comingSoonPayment.textButton',
          ),
          iconButton: this.translateService.instant(
            'i18n.payments.comingSoonPayment.iconButton',
          ),
        };

        this.paymentActivityTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.paymentActivity.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.paymentActivity.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.paymentActivity.textButton',
          ),
        };
        this.makePaymentTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.makePayment.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.makePayment.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.makePayment.textButton',
          ),
        };
        this.paymentPreferencesTile = {
          platformEnum: this.platformService?.platformType,
          isTouch: this.platformService.isTouch?.value,
          title: this.translateService.instant(
            'i18n.payments.paymentPreferences.title',
          ),
          textBody: this.translateService.instant(
            'i18n.payments.paymentPreferences.textBody',
          ),
          textButton: this.translateService.instant(
            'i18n.payments.paymentPreferences.textButton',
          ),
        };

        this.recentInvoicesAndCreditsText = {
          title: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.title',
          ),
          credits: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.credits',
          ),
          invoices: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.invoices',
          ),
          customer: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.customer',
          ),
          transaction: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.transaction',
          ),
          custPo: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.custPo',
          ),
          issueDate: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.issueDate',
          ),
          amount: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.amount',
          ),
          amountDue: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.amountDue',
          ),
          dueDate: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.dueDate',
          ),
          status: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.status',
          ),
          customerCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.customerCut',
          ),
          transactionCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.transactionCut',
          ),
          issueDateCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.issueDateCut',
          ),
          amountCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.amountCut',
          ),
          amountDueCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.amountDueCut',
          ),
          statusCut: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.statusCut',
          ),
          noRecentActivity: this.translateService.instant(
            'i18n.payments.recentInvoicesCredits.noRecentActivity',
          ),
          view: this.translateService.instant('i18n.common.view'),
          pay: this.translateService.instant('i18n.common.pay'),
          download: this.translateService.instant('i18n.common.download'),
        };

        this.transactionRowText = {
          invoice: this.translateService.instant(
            'i18n.payments.transactionRow.invoice',
          ),
          custPo: this.translateService.instant(
            'i18n.payments.transactionRow.custPo',
          ),
          issueDate: this.translateService.instant(
            'i18n.payments.transactionRow.issueDate',
          ),
          issueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.issueDateCut',
          ),
          amount: this.translateService.instant(
            'i18n.payments.transactionRow.amount',
          ),
          dueDate: this.translateService.instant(
            'i18n.payments.transactionRow.dueDate',
          ),
          amountDue: this.translateService.instant(
            'i18n.payments.transactionRow.amountDue',
          ),
          dueDateCut: this.translateService.instant(
            'i18n.payments.transactionRow.dueDateCut',
          ),
          amountCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountCut',
          ),
          amountDueCut: this.translateService.instant(
            'i18n.payments.transactionRow.amountDueCut',
          ),
          pay: this.translateService.instant('i18n.common.pay'),
          paymentAmount: this.translateService.instant(
            'i18n.payments.transactionRow.paymentAmount',
          ),
        };
        this.maxTransactionsSelectedMessage = this.translateService.instant(
          'i18n.payments.makePayment.maximumInvoicesSelected',
          { maximumSelected: MAX_INVOICE_SELECTION },
        );
      });
  }

  onCustomerSwitch(customers) {
    this.paymentsService.switchPaymentCustomer(customers);
  }

  async openMobileInvoicesCreditsOptions({
    event,
    transaction,
    showPayButton: showPayButton,
  }: {
    event?: Event;
    transaction: PaymentTransactionsViewModel;
    showPayButton: boolean;
  }) {
    let buttons: any[] = [];

    if (this.showMakePayments && transaction.isPayable && showPayButton) {
      buttons.push({
        text: this.translateService.instant('i18n.common.pay'),
        handler: () => {
          this.payInvoice(transaction);
          this.invoiceIsrService.dismissPopover();
        },
      });
    }

    this.cancelPaymentsService
      .createCancelOption(transaction)
      .subscribe(cancelButton => {
        !!cancelButton && buttons.push(cancelButton);
        this.invoiceIsrService.openInvoiceOptions(
          {
            event,
            transaction,
          },
          buttons,
        );
      });
  }

  goToAllInvoices(showInvoicesDue = false) {
    const queryParams = {};
    if (showInvoicesDue) {
      queryParams[InvoiceFilterIds.Status] = [
        `${Status.OVERDUE},${Status.PARTIALLY_PAID},${Status.UNPAID}`,
      ];
    }
    this.routeTo(PATHS.PAYMENTS_ALL_INVOICES, queryParams);
  }

  goToMakeAPayment() {
    this.routeTo(PATHS.PAYMENTS_MAKE_PAYMENT);
  }

  goToPaymentActivity() {
    this.routeTo(PATHS.PAYMENTS_PAYMENT_ACTIVITY);
  }

  goToPaymentPreferences(openAutoPayModal = false) {
    this.paymentsPageVM$.pipe(take(1)).subscribe(vm => {
      if (openAutoPayModal) {
        if (!vm.isAutopayOn) {
          this.paymentsService.goToLegacyPlatform(
            this.platformService.isTouch.value,
          );
          return;
        }
        const navigationExtras: NavigationExtras = {};
        navigationExtras.state = {};
        navigationExtras.state[OPEN_AUTOPAY_MODAL] = true;
        this.router.navigate(
          [PATHS.PAYMENTS_PAYMENT_PREFERENCES],
          navigationExtras,
        );
      } else {
        this.routeTo(PATHS.PAYMENTS_PAYMENT_PREFERENCES);
      }
    });
  }

  goToAdjustPayment() {
    this.routeTo(PATHS.PAYMENTS_ADJUST_PAYMENT);
  }

  goToLegacyPlatform(isSuperUser?: boolean) {
    if (isSuperUser) {
      return;
    }
    this.paymentsService.goToLegacyPlatform(this.platformService.isTouch.value);
  }

  goToPayAccountBalance() {
    if (this.showMakePayments) {
      this.paymentsService.setBalancePaymentInvoices(showError => {
        const navigationExtras: NavigationExtras = {};
        if (showError) {
          navigationExtras.state = {};
          navigationExtras.state[INITIAL_TOAST_ERROR_MESSAGE] =
            this.maxTransactionsSelectedMessage;
        }
        this.router.navigate([PATHS.PAYMENTS_ADJUST_PAYMENT], navigationExtras);
      });
    } else {
      this.goToLegacyPlatform();
    }
  }

  payInvoice(invoice: PaymentTransactionsViewModel) {
    this.paymentsService.clearSelectedInvoices();
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.paymentsService
        .fetchTranslatedIds([invoice._id])
        .pipe(take(1))
        .subscribe({
          next: res => {
            let invoiceIds: [
              { activeInvoiceID: string; allInvoiceID: string },
            ] = res.invoiceIDs;
            let translatedIDs: Dictionary<string> = {};
            invoiceIds.forEach(ids => {
              translatedIDs[ids.allInvoiceID] = ids.activeInvoiceID;
            });
            this.paymentsService.selectInvoice(invoice, translatedIDs);
            this.loadingSpinnerService.dismissSpinnerModal();
            this.goToAdjustPayment();
          },
          error: _e => {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.paymentsService.showToastMessage(
              this.translateService.instant(
                'i18n.payments.reviewAndSubmit.failedSubmitErrors.fpgn-1002',
              ),
            );
          },
        });
    });
  }

  async viewInvoice(transaction: PaymentTransactionsViewModel) {
    this.invoiceIsrService.viewInvoice(
      transaction.isr.docID.toString(),
      transaction.transactionNumber,
      transaction.secondaryTransactionNumber,
    );
  }
}
